import { Component, OnInit, Input, Output, EventEmitter, ViewContainerRef, forwardRef, SimpleChanges } from '@angular/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { environment } from 'environments/environment';
import { ErrorDO } from 'app/BaseFramework/shared/models/error.model';
import * as dict from 'spelling/dictionaries/en_US.js'
import * as spelling from 'spelling/'
import {
  ValidationUtilService,
  LookupDataService,
  CommonUtilty,
  MultiLingualService,
  InteractService,
  ComponentOutputDO,
  AppMetadataHandler
} from 'app'
import { FormBuilder, FormGroup, ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormGroup } from '@angular/forms';
import * as moment from 'moment/moment';
import { delay } from 'rxjs/operators';
import { of } from 'rxjs';

interface ValidationOutput {
  isInvalid: boolean,
  errorDOs: Array<ErrorDO>
}

/**
 * Dynamic Form Component
 * 
 * A reusable component that generates forms dynamically based on metadata configuration.
 * Supports various field types including input, combo, date, textarea, etc.
 * Implements ControlValueAccessor for seamless integration with Angular forms.
 * 
 * Usage Example:
 * <app-dynamic-form 
 *   FORMNAME="submissionDataForm" 
 *   FORMTITLE="Addition Fields" 
 *   [IO]="payloadInEdit"
 *   [currentComponentName]="'QuestionnaireComponent'" 
 *   [formMetaData]="formMetaData"
 *   (componentOutput)="_BaseEmitHandler($event, 'QuestionnaireComponent')">
 * </app-dynamic-form>
 */
@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DynamicFormComponent),
      multi: true
    }
  ]
})
export class DynamicFormComponent implements OnInit, ControlValueAccessor {

  // ===========================
  // COMPONENT PROPERTIES
  // ===========================
  
  baseComponentName = `DynamicFormComponent`
  _currentComponentName = `DynamicFormComponent`
  environment = environment
  FormObject: FormGroup
  lookupsToBind = ['clmEventTypeCd', 'clmCauseOfDeathCd', 'clmMannerOfLossCd']
  selectorOfThisComponent = 'app-claims-basic-details'
  parentEvent$
  lookup$;
  lookupData = {};
  errorList: any = [];
  metaDataValid = true
  @Input() fetchingFormData;
  FORMMETADATAFACTORY: any = []
  rulesExecutionMetaData
  changeDetectionConsoleStyle = 'color: blue; background:FFF; padding: 2px 5px; border-radius: 2px'
  
  currencyOptions = {
    prefix: this.appMetadataHandler.baseCurrencyPREFIX,
    thousands: this.appMetadataHandler.THOUSANDS_SEPARATOR,
    decimal: this.appMetadataHandler.DECIMAL_SEPARATOR,
    align: this.appMetadataHandler.currencyAlignment
  }

  panelCustomStyle = {
    'background': 'transparent',
    'border': 'none',
    'border-bottom': '1px solid #dfdfdf',
    'border-radius': '0',
    'margin-bottom': '14px',
    'line-height': '1rem',
    'padding': '0 0 0px',
    'margin': 0,
  }

  FormFieldNameMap = {
    eventType: "Event Type",
    eventDate: "Event Date",
    retypeEventDate: "Retype Event Date",
    eventTime: "Event Time",
    retypeEventTime: "Retype Time",
    causeOfDeath: "Cause of Death",
    intimationDt: "Intimation Date",
    receivedDt: "Received Date",
  }

  // ===========================
  // INPUT/OUTPUT PROPERTIES
  // ===========================

  @Output() componentOutput = new EventEmitter<any>()
  @Input() FORMNAME: string;
  @Input() FORMTITLE: string;
  @Input() headingIco: string;
  @Input() gridClass: string;
  @Input() disabled: boolean;
  @Input() formIcon: string = "profile";
  @Input() enableCollapse: boolean = false;
  @Input() showArrow: boolean = false;
  @Input() emitInvalidComponentData: boolean = false;
  @Input() emitComponentDataInNgModel: boolean = false;
  @Input() dynamicRenderingMode: boolean = false;
  @Input() extractFromDOCoverage = false;
  @Input() mode = "sync";
  @Input() maxDepth
  @Input() baseDOName
  @Input() notStrictValidations
  @Input() outputMode: any;
  @Input() lookupLoadingMode: 'bySubject' | 'byData' = 'bySubject';

  public entityGuid
  private flattenedIO
  private _IO
  private _inputDataForEdit = null;
  private _extractElementFromArray
  private _EnableExtractAdditionalDOs
  private _additionalAttributeValueKeyName

  @Input()
  set currentComponentName(value) {
    if (value) this._currentComponentName = value;
  }

  @Input()
  set formMetaData(metaData) {
    if (this.commonUtilty.isArrayIterable(metaData)) {
      this.FORMMETADATAFACTORY = this.commonUtilty.sortObject('sortOrder', metaData.map(item => {
        if (!item.fieldMapping) {
          item = this.attributeMapper(item, 'fieldKey', 'fieldMapping')
        }
        return item
      }), 'asc')
      console.log("form meta data for police details", JSON.stringify(this.FORMMETADATAFACTORY));
    }
  }

  @Input()
  set ExtractElementFromArray(value) {
    this._extractElementFromArray = value
  }

  get ExtractElementFromArray() {
    return this._extractElementFromArray
  }

  get flatteningSafetyValue() {
    if (this._extractElementFromArray) {
      return 'unsafe'
    }
    else {
      return 'safe'
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['fetchingFormData']) {
      console.log('fetchingFormData input changed:', changes['fetchingFormData'].currentValue);
    }
  }

  @Input()
  set EnableExtractAdditionalDOs(value: any) {
    if (value) {
      if (!this.ExtractElementFromArray || !this.maxDepth) {
        console.error(`Failed to set EnableExtractAdditionalDOs on ${this._currentComponentName}`)
        console.error(`Config Issue for ${this._currentComponentName}: It's mandatory to set ExtractElementFromArray and maxDepth to use EnableExtractAdditionalDOs`)
      }
      else {
        this._EnableExtractAdditionalDOs = value
      }
    }
  }

  get EnableExtractAdditionalDOs() {
    return !!this._EnableExtractAdditionalDOs
  }

  @Input()
  set IO(value: any) {
    if (value) {
      this._IO = value
      if (!this.baseDOName) {
        this.baseDOName = this.commonUtilty.getDOName(this._IO)
      }
      if (value[this.baseDOName])
        this.entityGuid = value[this.baseDOName].guid
      else
        console.error(`Unsupported DOName for ${this.currentComponentName}`, this.baseDOName, value)
      this.flattenedIO = this.commonUtilty.flattenObject(this._IO, this.flatteningSafetyValue, this.maxDepth)
    }
  }

  get IO() {
    return this._IO
  }

  @Input()
  set inputDataForEdit(value: any) {
    if (value) this._inputDataForEdit = value;
  };

  @Input()
  set additionalAttributeValueKeyName(value) {
    this.additionalAttributeValueKeyName = value || 'attributeValue'
  }

  get additionalAttributeValueKeyName() {
    return this._additionalAttributeValueKeyName
  }

  // ===========================
  // CONSTRUCTOR
  // ===========================

  constructor(
    private viewContainerRef: ViewContainerRef,
    public validationUtilService: ValidationUtilService,
    private fb: FormBuilder,
    private commonUtilty: CommonUtilty,
    private multiLingualService: MultiLingualService,
    private lookupDataService: LookupDataService,
    private interactService: InteractService,
    public appMetadataHandler: AppMetadataHandler,
    private msg: NzMessageService
  ) { }

  // ===========================
  // CONTROL VALUE ACCESSOR IMPLEMENTATION
  // ===========================

  /**
   * Implements ControlValueAccessor writeValue method
   */
  writeValue(obj: any): void {
    if (obj === 'nullify') {
      this.FormObject.reset()
      this.propagateChange(null)
    }
  }

  propagateChange = (_: any) => { };

  /**
   * Registers onChange callback for ControlValueAccessor
   */
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  /**
   * Registers onTouched callback for ControlValueAccessor
   */
  registerOnTouched(fn: any): void {
  }

  /**
   * Sets disabled state for ControlValueAccessor
   */
  setDisabledState?(isDisabled: boolean): void { }

  // ===========================
  // LIFECYCLE METHODS
  // ===========================

  /**
   * Component initialization lifecycle hook
   */
  ngOnInit() {
    const consoleStyle = 'color: red; padding: 2px 5px; border-radius: 2px'
    if (environment && !environment.production) {
      console.group('%cClaims Basic Details : Usage detected on ' + this.getParentComponentName(), 'color: white; background-color: orange; padding: 2px 5px; border-radius: 2px');
      console.log(`%c<${this.getCurrentComponentName()}></${this.getCurrentComponentName()}>, has been used. `, consoleStyle)
      console.log("%cYou can override default title, message and icon(ng-zorro icon name).", consoleStyle)
      console.log(`%cExample , <${this.getCurrentComponentName()} (componentOutput)='doSomethingOnDataReceived()'></${this.getCurrentComponentName()}>`, consoleStyle)
      console.groupEnd();
    }

    if (!this.FORMNAME || !this.FORMMETADATAFACTORY) {
      this.metaDataValid = false
    }

    this.FORMMETADATAFACTORY.forEach(metaData => {
      // Handle field mapping replacement when generic mapping and baseDOName are present
      if (metaData.fieldGenericMapping && this.baseDOName && !metaData.ignoreBaseDO && metaData.fieldMapping) {
        let oldFieldMapping = metaData.fieldMapping.split('-')[0];
        oldFieldMapping = oldFieldMapping.replace(/DO/g, '');
        let replaceFieldMapping = this.baseDOName.replace(/DO/g, '');
        const pattern = new RegExp(oldFieldMapping, 'g');  // dynamic regex
        metaData.fieldMapping = metaData.fieldMapping.replace(pattern, replaceFieldMapping);
      }
      // Set hidden property based on columnVisibility
      if (typeof (metaData.columnVisibility) == 'boolean') { //Assumption: columnVisibility will always come from lightGrid and it will not have hidden property unless we will inject from below.
        metaData.hidden = !metaData.columnVisibility;
      }
    })

    if (this.mode == 'async') {
      this.fetchMetaData().subscribe(resp => {
        this.actOnMetaData(resp);
      })
    } else {
      this.actOnMetaData(this.FORMMETADATAFACTORY);
    }

    this.subscribeToParentEvent()
  }

  // ===========================
  // COMPONENT UTILITY METHODS
  // ===========================

  /**
   * Emits component output with proper transformation
   */
  EmitComponentOutput(data) {
    let c = new ComponentOutputDO(this.getCurrentComponentName(), data, this.componentOutput, this.UItoServerTransform, this.baseComponentName)
    return c;
  }

  /**
   * Gets the parent component instance
   */
  getParentComponent(): Component {
    try {
      return this.viewContainerRef['_data'].componentView.component.viewContainerRef['_view'].component
    } catch { }
  }

  /**
   * Gets the parent component name
   */
  getParentComponentName(): string {
    try {
      return this.viewContainerRef['_data'].componentView.component.viewContainerRef['_view'].component.constructor.name
    } catch { }
  }

  /**
   * Gets the current component name
   */
  getCurrentComponentName(): string {
    return `${this._currentComponentName}`
  }

  /**
   * Maps attributes from source to target
   */
  private attributeMapper(extractedConfig: any, sourceAttr, targetAttr) {
    if (extractedConfig[sourceAttr])
      extractedConfig[targetAttr] = extractedConfig[sourceAttr];
    return extractedConfig
  }

  // ===========================
  // METADATA AND FORM INITIALIZATION
  // ===========================

  /**
   * Acts on metadata to initialize form and lookups
   */
  private actOnMetaData(resp: any) {
    if (this.lookupLoadingMode == 'bySubject') {
      this.lookup$ = this.initializeLookupSubjects(resp);
    } else {
      this.initializeLookupData(resp);
    }
    let metaData;
    if (this.EnableExtractAdditionalDOs)
      metaData = this.commonUtilty.deepCopy(this.setFieldMappingForAdditionalDOs(resp, this.IO));
    else
      metaData = this.commonUtilty.deepCopy(resp);
    this.FormObject = this.formFactory(metaData, this.FORMNAME, this.flattenedIO);
    this.rulesExecutionMetaData = this.validateAndExecuteRules(resp);
    this.applyFieldRuleOnFields(metaData)

    // Make sure to call detectChangeAndPropogateData after form is fully initialized
    setTimeout(()=>this.detectChangeAndPropogateData(this.FormObject));
  }

  /**
   * Fetches metadata asynchronously
   */
  fetchMetaData() {
    return of(this.FORMMETADATAFACTORY).pipe(delay(100))
  }

  /**
   * Initializes lookup subjects for reactive data loading
   */
  private initializeLookupSubjects(metaData) {
    if (metaData && Array.isArray(metaData))
      metaData.forEach(field => { this.lookupsToBind.push(field.lookupCd); });
    let lookup$ = this.commonUtilty.initializeLookupList(this.lookupsToBind, metaData);
    return lookup$
  }

  /**
   * Initializes lookup data for static data loading
   */
  private initializeLookupData(metaData) {
    if (metaData && Array.isArray(metaData))
      metaData.forEach(field => { this.lookupsToBind.push(field.lookupCd); });
    this.initializeLookupListWithData(this.lookupsToBind, metaData);
  }

  /**
   * Initializes lookup list with data
   */
  initializeLookupListWithData(lookupList, metaData?) {
    if (lookupList && lookupList.length > 0) {
      lookupList.forEach(lookup => {
        if (lookup) {
          let filteredMetaData = metaData ? metaData.filter(item => item.lookupCd == lookup) : null;
          let filteredLookupCSV = this.commonUtilty.isArrayIterable(filteredMetaData) ? filteredMetaData.find(() => true).allowedLookUpCdCSV ? filteredMetaData.find(() => true).allowedLookUpCdCSV.split(',') : null : null;
          this.lookupDataService.getLookup(lookup, false).subscribe(item => {
            this.lookupData[lookup] = this.filterLookupData(item, filteredLookupCSV);
          });
        }
      })
    }
  }

  /**
   * Filters lookup data based on criteria
   */
  filterLookupData(lookupList, criteriaFilteredLookup?) {
    let output = [];
    if (criteriaFilteredLookup) {
      for (var i = 0; i < lookupList.length; i++) {
        if (criteriaFilteredLookup.indexOf(lookupList[i].lookupKey) !== -1) {
          output.push(lookupList[i]);
        }
      }
    } else {
      output = JSON.parse(JSON.stringify(lookupList));
    }
    return output.sort((a, b) => a.sortOrder - b.sortOrder);
  }

  // ===========================
  // FORM FACTORY AND VALIDATION
  // ===========================

  /**
   * Creates form controls based on metadata configuration
   */
  formFactory(metaData, FormName, IO = {}) {
    let formDataOuput = {}
    if (metaData && Array.isArray(metaData)) {
      metaData.forEach((field, index) => {
        let intialValue
        let dictspell = new spelling(dict)
        let output = this.commonUtilty.splitMulti(field.fieldName, ['/', ' ', '-', '(', ')'])
        let wrongWords = [];
        let didYouMean = []
        if (Array.isArray(output)) {
          output.forEach(word => {
            if (!dictspell.lookup(word)['found']) {
              wrongWords.push(word)
              let suggestions = dictspell.lookup(word)['suggestions']
              if (this.commonUtilty.isArrayIterable(suggestions)) {
                didYouMean = suggestions.map(a => a.word);
              }
            }
          })
        }
        // { found: true, word: 'he', rank: 38410 }
        let comboCondition1 = !!(field.fieldType == 'COMBO' && field.comboMode == 'multiple' && field.comboBindingType == 'CSV')
        let comboCondition2 = !!(field.fieldType == 'COMBOMULTISELECTCSV' || field.fieldType == 'COMBOMULTISELECTENTERABLECSV')

        this.FORMMETADATAFACTORY[index].fieldNameSpellCheckCorrent = !this.commonUtilty.isArrayIterable(wrongWords)
        this.FORMMETADATAFACTORY[index].wrongSpellWords = `Wrong Ones - ${wrongWords.join(', ')}. Did you mean? - ${didYouMean.join(', ')}`;

        if (this.extractFromDOCoverage) {
          intialValue = this.formExtractFromDO(IO, field)
        }
        else {
          if (field.fieldType == 'DATE') {
            let data = IO[field.fieldMapping] || (field.defaultValue == "NOW" ? this.commonUtilty.UIDOtoServerDODate(new Date()) : field.defaultValue)
            if (this.commonUtilty.isArrayIterable(data))
              intialValue = this.commonUtilty.serverDOtoUIDOsetTime(data)
            else if (data && data.includes(":")) {
              let array = data.split(":").map(item => {
                return Number(item);
              })

              intialValue = this.commonUtilty.serverDOtoUIDOsetTime(array)
            }
          }
          else if (field.fieldType == 'TIME') {
            let data = IO[field.fieldMapping] || field.defaultValue
            intialValue = this.commonUtilty.serverDOtoUIDOsetTime(data)
          }
          else if (field.fieldType == 'EMPTY') {
            //No Reaction of EMPTY
          }
          else if (comboCondition1 || comboCondition2) {
            let data = IO[field.fieldMapping] || field.defaultValue
            intialValue = this.commonUtilty.convertCSVToArray(data)
          }
          else {
            intialValue = IO[field.fieldMapping] || field.defaultValue
          }
        }
        this.FormFieldNameMap[field.fieldKey] = field.fieldName;
        formDataOuput[field.fieldKey] = this.validationUtilService.setValidationsForFormControl(
          field.fieldKey, FormName, intialValue, null)
      })
    }
    let output = this.fb.group(formDataOuput)
    return output
  }

  /**
   * Extracts form data from DO coverage
   */
  formExtractFromDO(IO, field) {
    let comboCondition3 = (field.fieldType == 'COMBO' || field.fieldType == 'CURRENCYINPUT' || field.fieldType == 'INPUT')
    let index = this.getIndex(field.fieldMapping)
    let intialValue = null;
    if(index == null) {
      intialValue = IO[field.fieldMapping]
    }
    else if (comboCondition3 && IO && IO["proposalDO-proposalCoverages"]
      && IO["proposalDO-proposalCoverages"][index] &&
      (IO["proposalDO-proposalCoverages"][index][field.fieldKey] || !IO["proposalDO-proposalCoverages"][index][field.fieldKey])) {
      intialValue = IO["proposalDO-proposalCoverages"][index][field.fieldKey];
    }
    else if (comboCondition3 && IO && IO["caseDO-caseCoverages"]
      && IO["caseDO-caseCoverages"][index] &&
      (IO["caseDO-caseCoverages"][index][field.fieldKey] || !IO["caseDO-caseCoverages"][index][field.fieldKey])) {
      intialValue = IO["caseDO-caseCoverages"][index][field.fieldKey];
    }
    return intialValue;
  }

  /**
   * Extracts index from mapping string
   */
  getIndex(mapping) {
    const str = mapping;
    const match = str.match(/\d+/);
    let extractedNumber: number | undefined;
    if (match) {
      extractedNumber = parseInt(match[0]);
    }
    return extractedNumber;
  }

  // ===========================
  // RULES AND VALIDATION ENGINE
  // ===========================

  /**
   * Validates and executes field rules
   */
  validateAndExecuteRules(metaData) {
    let output = {
      fieldsOnWatch: {},
      metaData: null
    }
    if (this.commonUtilty.isArrayIterable(metaData)) {
      metaData.forEach(field => {
        if (this.commonUtilty.isArrayIterable(field.fieldRules)) {
          field.fieldRules.forEach(fieldRule => {
            if (!output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping]) {
              let sourceFieldMetaData = metaData.find(md => md.fieldGenericMapping == fieldRule.sourceFieldGenericMapping)
              if (sourceFieldMetaData) {
                output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping] = this.commonUtilty.deepCopy(sourceFieldMetaData)
              }
              else {
                console.error(`DynamicForm Configuration Issue - sourceField:${fieldRule.sourceFieldGenericMapping} not found in metaData`, field)
              }
            }
            let evaluatedFunction = (sourceFieldKey, ruleExec, metaData) => {
              let targetAttribute = ruleExec['targetAttribute'];
              let fieldIndex = metaData.findIndex(metaDataItem => metaDataItem.fieldKey == field.fieldKey)
              if (fieldIndex != -1) {
                if (targetAttribute == 'value' && this.FormObject) {
                  this.FormObject.controls[field.fieldKey].setValue(this.operationHandler(sourceFieldKey, fieldRule.expression, metaData, this.FormObject))
                }
                else if (targetAttribute == 'hidden' && this.FormObject) {
                  if (!(ruleExec && ruleExec.expression && this.commonUtilty.isArrayIterable(ruleExec.expression.data) &&
                    ruleExec.expression.data.includes(this.FormObject.value[sourceFieldKey]))) {
                    this.FormObject.controls[field.fieldKey].setValue(null);
                  }
                  this.FORMMETADATAFACTORY[fieldIndex][targetAttribute] = this.operationHandler(sourceFieldKey, fieldRule.expression, metaData, this.FormObject);
                }
                else {
                  this.FORMMETADATAFACTORY[fieldIndex][targetAttribute] = this.operationHandler(sourceFieldKey, fieldRule.expression, metaData, this.FormObject);
                }
              }
              return metaData
            }

            if (!output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping]) {
              output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping] = {}
            }
            if (output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping] && !this.commonUtilty.isArrayIterable(output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping]['rulesExecutionData'])) {
              output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping]['rulesExecutionData'] = []
            }
            output.fieldsOnWatch[fieldRule.sourceFieldGenericMapping]['rulesExecutionData'].push({ fieldExpressionExecultors: evaluatedFunction, ...fieldRule });
            output.metaData = this.commonUtilty.deepCopy(metaData) //Future Purpose
          });
        }
      });
    }
    return output
  }

  /**
   * Applies field rules on form fields
   */
  private applyFieldRuleOnFields(metaData: any) {
    if (this.commonUtilty.isArrayIterable(metaData)) {
      metaData.forEach(field => {
        this.fieldDataChangeHandler(field.fieldGenericMapping);
      });
    }
  }

  /**
   * Executes rules based on field changes
   */
  executeRules(fieldGenericMapping, metaData) {
    if (this.rulesExecutionMetaData.fieldsOnWatch[fieldGenericMapping]) {
      this.processLog(`Change detected on - ${this.rulesExecutionMetaData.fieldsOnWatch[fieldGenericMapping].fieldName} `)
      let previousMetaData = this.commonUtilty.deepCopy(metaData)
      let rulesExecutionData = this.rulesExecutionMetaData.fieldsOnWatch[fieldGenericMapping]['rulesExecutionData']

      if (this.commonUtilty.isArrayIterable(rulesExecutionData)) {
        rulesExecutionData.forEach(ruleExec => {
          let evaluatedFunction = ruleExec['fieldExpressionExecultors'].bind(this)
          let sourceMetaDataMap = this.commonUtilty.convertArrayToMap(metaData, 'fieldGenericMapping')
          metaData = evaluatedFunction(sourceMetaDataMap[fieldGenericMapping]['fieldKey'], ruleExec, metaData)

          if (JSON.stringify(previousMetaData) === JSON.stringify(metaData)) {
            this.processLog(`Change in ${fieldGenericMapping} has affected other fields`)
          }
        });
      }
    }
    return metaData
  }

  /**
   * Handles operations based on expressions
   */
  operationHandler(sourceFieldKey, expression, metaData, FORM) {
    let output = null
    if (expression && this.commonUtilty.isArrayIterable(metaData)) {
      //valueOf is going to be fieldGenericMapping
      if (expression.expectedValueOf && FORM) {
        let metaData_expectedValueOf = metaData.find(md => md.fieldGenericMapping == expression.expectedValueOf)
        let expectedValueOf = FORM.get(metaData_expectedValueOf.fieldKey).value
        if (!expression.data && Array.isArray(expression.data) && expectedValueOf) {
          expression.data = []
        }
        expression.data.push(expectedValueOf)
      }
      let sourceFieldData = FORM ? FORM.value[sourceFieldKey] : null;

      switch (expression.operation) {
        // EQUAL
        case '==':
          output = Array.isArray(expression.data) ? expression.data.includes(sourceFieldData) : expression.data == expression.data
          break;

        // NOTEQUAL
        case '!=':
          output = Array.isArray(expression.data) ? !expression.data.includes(sourceFieldData) : expression.data != expression.data
          break;

        // LESSTHAN
        case '>':
          output = Array.isArray(expression.data) ? expression.data.every(d => sourceFieldData > d) : expression.data > expression.data
          break;

        // GREATERTHAN
        case '<':
          output = Array.isArray(expression.data) ? expression.data.every(d => sourceFieldData < d) : expression.data < expression.data
          break;

        case 'DATEDIFF': {
          if (FORM && FORM.get(expression.DATE1).value && FORM.get(expression.DATE2).value) {
            let value = Math.abs(moment(FORM.get(expression.DATE1).value).diff(FORM.get(expression.DATE2).value, 'days'))
            output = isNaN(value) ? 0 : value
          }
          break;
        }
        case 'T2S': {
          if (sourceFieldData) {
            let value = this.multiLingualService.ConvertChineseT2S(sourceFieldData)
            output = value
          }
          break;
        }
        case 'S2T': {
          if (sourceFieldData) {
            let value = this.multiLingualService.ConvertChineseS2T(sourceFieldData)
            output = value
          }
          break;
        }

        default:
          this.processLog("Invalid Operation Metadata", 'error')
          break;
      }

    }
    else {
      console.error("Operation Handler: Invalid Expression or Invalid Metadata", expression, metaData)
    }

    return output
  }

  /**
   * Handles field data changes and triggers rules
   */
  fieldDataChangeHandler(fieldGenericMapping) {
    if (this.rulesExecutionMetaData && this.rulesExecutionMetaData.fieldsOnWatch[fieldGenericMapping]) {
      this.FORMMETADATAFACTORY = this.executeRules(fieldGenericMapping, this.FORMMETADATAFACTORY)
      this.lookup$ = this.initializeLookupSubjects(this.FORMMETADATAFACTORY);
    }
  }

  /**
   * Logs process messages with styling
   */
  processLog(msg, type?, Object?) {
    let logType = type || 'log'
    let msgType = type || 'info'
    let style = !type ? this.changeDetectionConsoleStyle : null
    if (!environment.production)
      this.msg[msgType](msg)
    if (Object)
      console[logType](`%c Process Log: ${msg}`, style, Object)
    else
      console[logType](`%c Process Log: ${msg}`, style)
  }

  // ===========================
  // DATA TRANSFORMATION METHODS
  // ===========================

  /**
   * Transforms UI data to server format
   */
  UItoServerTransform(payload, response, DOKeyName?, handleNullIO?) {
    if (handleNullIO && !payload) {
      payload = {};
    }
    let DOName
    if (!DOKeyName)
      DOName = this.commonUtilty.getDOName(payload)
    else
      DOName = DOKeyName;
    let flattenedIO = this.commonUtilty.plainflattenObject(payload, this.flatteningSafetyValue, this.maxDepth)
    let metaData
    if (this.EnableExtractAdditionalDOs)
      metaData = this.commonUtilty.setFieldMappingForAdditionalDOs(response.componentData.metaData, payload, this.additionalAttributeValueKeyName)
    else
      metaData = response.componentData.metaData
    let form = response.componentData.dynamicRenderingMode ? response.componentData.form : response.componentData.form.value
    if (flattenedIO && response && DOName && payload[DOName] && metaData && Array.isArray(metaData)) {
      metaData.forEach(field => {
        let comboCondition1 = !!(field.fieldType == 'COMBO' && field.comboMode == 'multiple' && field.comboBindingType == 'CSV')
        let comboCondition2 = !!(field.fieldType == 'COMBOMULTISELECTCSV' || field.fieldType == 'COMBOMULTISELECTENTERABLECSV')

        //check if field exits on form
        if (form.hasOwnProperty(field.fieldKey)) {
          if (field.fieldType == 'DATE' && !field.ignoreDateTimeConversion) {
            flattenedIO[field.fieldMapping] = this.commonUtilty.UIDOtoServerDODate(form[field.fieldKey])
          }
          else if (field.fieldType == 'TIME' && !field.ignoreDateTimeConversion) {
            flattenedIO[field.fieldMapping] = this.commonUtilty.UIDOtoServerDOsetTime(form[field.fieldKey])
          }
          else if (comboCondition1 || comboCondition2) {
            flattenedIO[field.fieldMapping] = this.commonUtilty.convertArrayToCSV(form[field.fieldKey])
          }
          else if (field.fieldType == 'EMPTY') {
            // No Reaction on EMPTY
          }
          else {
            flattenedIO[field.fieldMapping] = form[field.fieldKey]
          }
        }
      })
    } else {
      console.error("Dynamic Form Error:", response)
    }

    let output = this.commonUtilty.plainUnflattenObject(flattenedIO, this.flatteningSafetyValue, this.maxDepth)

    if (this.appMetadataHandler.featureFlags.enableTransformLogForSteps && environment.production) {
      console.log(`UIToServer Transformation ${response.componentName} : ${JSON.stringify(payload)}`)
    }
    return output
  }

  /**
   * Sets field mapping for additional DOs
   */
  private setFieldMappingForAdditionalDOs(metaData: any, nestedIO: any) {
    if (metaData && Array.isArray(metaData)) {
      metaData.forEach((field, index) => {
        if (field.fieldMapping && field.fieldMapping.includes('$additionalDetails')) {
          let attributes = this.commonUtilty.splitMulti(field.fieldMapping, ['-']) || [];
          let attributeCd = field.fieldKey;
          let attributeKeyName = attributes.slice(-2);

          let newBaseMapping = this.commonUtilty.truncateAfter(field.fieldMapping, '$additionalDetails').replace('$', '0-');
          let newMapping;
          if (newBaseMapping && nestedIO && nestedIO[newBaseMapping] && Array.isArray(nestedIO[newBaseMapping])) {
            let foundIndex = nestedIO[newBaseMapping].findIndex(element => element[attributeKeyName] == attributeCd);
            if (foundIndex != -1) {
              newMapping = `${newBaseMapping}-${foundIndex}-${this.additionalAttributeValueKeyName}`;
            }
            else {
              let newIndex = nestedIO[newBaseMapping].length;
              newMapping = `${newBaseMapping}-${newIndex}-${this.additionalAttributeValueKeyName}`;
            }
          }
          if (newMapping) {
            metaData[index]['oldFieldMapping'] = field.fieldMapping;
            field.fieldMapping = newMapping;
            metaData[index]['fieldMapping'] = newMapping;
            metaData[index]['attributeKeyName'] = attributeKeyName
            metaData[index]['isAdditionalDO'] = true
            metaData[index]['additionalAttributeValueKeyName'] = this.additionalAttributeValueKeyName
          }
        }
      })
    }
    return metaData
  }

  // ===========================
  // EVENT HANDLING AND SUBSCRIPTION
  // ===========================

  /**
   * Subscribes to parent component events
   */
  subscribeToParentEvent() {
    if (this.parentEvent$ != null)
      this.parentEvent$.unsubscribe()
    this.parentEvent$ = this.interactService.pingSubject$.subscribe((param: any) => {
      const matches = param.filter(s => s.includes(this.getCurrentComponentName()));
      if (this.commonUtilty.isArrayIterable(param) && this.commonUtilty.isArrayIterable(matches)) {
        let validationResult = this.validationUtilService.validateOutput(this.FormObject, this.FORMNAME, this._currentComponentName);
        if (!validationResult.isInvalid) {
          let output = { metaData: this.FORMMETADATAFACTORY, form: this.FormObject }
          this.EmitComponentOutput(output)
        }
        else {
          this.EmitComponentOutput(validationResult)
        }
      }
      else {
        console.warn(`${this.getCurrentComponentName()} recieved the ping, but is not a emitter child for current transaction type.`)
      }
    })
  }

  /**
   * Detects form changes and propagates data
   */
  detectChangeAndPropogateData(form: UntypedFormGroup) {
    if (form) {
      let componentFirstOutput = this.EmitComponentOutput({ metaData: this.FORMMETADATAFACTORY, form: this.FormObject })
      let firstOutput = this.UItoServerTransform(this.IO, componentFirstOutput, this.baseDOName, true)
      if (this.outputMode && this.outputMode == 'ComponentOutput') {
        this.getDataToPropagate(componentFirstOutput)
      } else
        this.getDataToPropagate(firstOutput)
      form.valueChanges.subscribe(() => {
        let componentOuput = this.EmitComponentOutput({ metaData: this.FORMMETADATAFACTORY, form: this.FormObject })
        let output = this.UItoServerTransform(this.IO, componentOuput, this.baseDOName, true)
        if (this.outputMode && this.outputMode == 'ComponentOutput') {
          this.getDataToPropagate(componentOuput)
        } else {
          this.getDataToPropagate(output)
        }
      })
    }
    else {
      console.error(`${this._currentComponentName} - Form Object is missing`)
    }
  }

  /**
   * Gets data to propagate to parent component
   */
  private getDataToPropagate(componentData: ComponentOutputDO) {
    console.log(`[${this._currentComponentName}] Propagating data to parent`);
    if (this.emitInvalidComponentData) {
      return this.propagateChange(Object.assign({}, componentData));
    } else {
      // Use the FormObject directly to get form values
      const formValues = this.FormObject ? this.FormObject.value : {};
      this.propagateChange(formValues);
    }
  }

  // ===========================
  // UTILITY METHODS
  // ===========================

  /**
   * Evaluates expressions dynamically
   */
  expressionEval(expression) {
    const result = (() => {
      return eval(expression.value);
    })();
    return result
  }

}